// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel';
import type { LatLngModelType } from 'common/models/flow/LatLng';
import type { StatusEnum } from './MasterLocation';
import type { TeamIdModelType } from './TeamId';
import type { LocationIdModelType } from './LocationId';
import type { ZoneMapGeometryModelType } from './ZoneMapGeometry';
import type { ZoneMapFolderModelType } from './ZoneMapFolder';
import type { ActorModelType } from './Actor';
import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';

import { TeamId } from './TeamId';
import { LocationId } from './LocationId';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import { ZoneMapFolder } from './ZoneMapFolder';
import { Actor } from './Actor';
import { AuditMetadata } from 'common/models/flow/AuditMetadata';

export type ZoneMapModelType = {
    clientId: string,
    id: string,
    name: string,
    status: StatusEnum,
    version: number,
    customProperties: { [key: string]: string },
    teamId: TeamIdModelType,
    homebaseId: LocationIdModelType,
    geometries: Array<ZoneMapGeometryModelType>,
    folders: Array<ZoneMapFolderModelType>,
    verified: boolean,
    isActiveVersion: boolean,
    createdOn: string,
    updatedOn: string,
    actor: ActorModelType,
    summary: {
        uniqueRidersCount: string,
        zonesCount: string,
        uniqueVehiclesCount: string,
        uniqueVehicleModelsCount: string
    },
    defaultCenter: ?LatLngModelType,
    auditMetadata: ?AuditMetadataModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ZoneMap extends ImmutableModel {
    static fromJS(json: ZoneMapModelType): ZoneMap {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.teamId = TeamId.fromJS(state.teamId);
        state.homebaseId = LocationId.fromJS(state.homebaseId);
        state.geometries = Immutable.List(state.geometries).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.folders = Immutable.List(state.folders).map(item =>
            ZoneMapFolder.fromJS(item)
        );
        state.actor = Actor.fromJS(state.actor);
        state.defaultCenter = state.defaultCenter
            ? LatLng.fromJS(state.defaultCenter)
            : state.defaultCenter;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    toJS(): ZoneMapModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            status: this.status,
            version: this.version,
            customProperties: this.customProperties.toObject(),
            teamId: this.teamId.toJS(),
            homebaseId: this.homebaseId.toJS(),
            geometries: this.geometries.toArray().map(item => item.toJS()),
            folders: this.folders.toArray().map(item => item.toJS()),
            verified: this.verified,
            isActiveVersion: this.isActiveVersion,
            createdOn: this.createdOn,
            updatedOn: this.updatedOn,
            actor: this.actor.toJS(),
            summary: this.summary,
            defaultCenter: this.defaultCenter
                ? this.defaultCenter.toJS()
                : this.defaultCenter,
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get teamId(): TeamId {
        return this._state.get('teamId');
    }

    setTeamId(teamId: TeamId): this {
        return this.clone(this._state.set('teamId', teamId));
    }

    get homebaseId(): LocationId {
        return this._state.get('homebaseId');
    }

    setHomebaseId(homebaseId: LocationId): this {
        return this.clone(this._state.set('homebaseId', homebaseId));
    }

    get geometries(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('geometries');
    }

    setGeometries(geometries: Immutable.List<ZoneMapGeometry>): this {
        return this.clone(this._state.set('geometries', geometries));
    }

    get folders(): Immutable.List<ZoneMapFolder> {
        return this._state.get('folders');
    }

    setFolders(folders: Immutable.List<ZoneMapFolder>): this {
        return this.clone(this._state.set('folders', folders));
    }

    get verified(): boolean {
        return this._state.get('verified');
    }

    setVerified(verified: boolean): this {
        return this.clone(this._state.set('verified', verified));
    }

    get isActiveVersion(): boolean {
        return this._state.get('isActiveVersion');
    }

    setIsActiveVersion(isActiveVersion: boolean): this {
        return this.clone(this._state.set('isActiveVersion', isActiveVersion));
    }

    get createdOn(): string {
        return this._state.get('createdOn');
    }

    setCreatedOn(createdOn: string): this {
        return this.clone(this._state.set('createdOn', createdOn));
    }

    get updatedOn(): string {
        return this._state.get('updatedOn');
    }

    setUpdatedOn(updatedOn: string): this {
        return this.clone(this._state.set('updatedOn', updatedOn));
    }

    get actor(): Actor {
        return this._state.get('actor');
    }

    setActor(actor: Actor): this {
        return this.clone(this._state.set('actor', actor));
    }

    get summary(): {
        uniqueRidersCount: string,
        zonesCount: string,
        uniqueVehiclesCount: string,
        uniqueVehicleModelsCount: string
    } {
        return this._state.get('summary');
    }

    setSummary(summary: {
        uniqueRidersCount: string,
        zonesCount: string,
        uniqueVehiclesCount: string,
        uniqueVehicleModelsCount: string
    }): this {
        return this.clone(this._state.set('summary', summary));
    }

    get defaultCenter(): ?LatLng {
        return this._state.get('defaultCenter');
    }

    setDefaultCenter(defaultCenter: ?LatLng): this {
        return this.clone(this._state.set('defaultCenter', defaultCenter));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }
}
